<template>
  <ScrollTop v-if="showScrollTop" />
  <div class="englobe-params">
    <div class="params-row top">
      <FiltersMenu
        :active-filters="activeFilters"
        :is-loading="store.loading"
        :disabled="!store.filters"
        @remove-filter="removeFilterItem"
        @reset-all-filters="resetAllFilters"
      >
        <SearchAsYouTypeFilter
          v-if="!store.isFollawedActive"
          filter="authors"
          :items="store.authors"
          item-label="name"
          item-value="_id"
          :active-filters="selectedFilters.authors"
          :is-loading="store.isAuthorFilterLoading"
          @search="fetchSearchAuthors"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetAuthorFilter"
        >
          Auteurs
        </SearchAsYouTypeFilter>
        <SelectMultipleCounter
          v-for="(value, key) in filtersNames"
          :key="key"
          :filter="key"
          :items="store.filters[key]"
          :active-filters="selectedFilters[key]"
          :has-search="store.filters[key]?.length > 10"
          :disabled="!store.filters[key] || !store.filters[key].length"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetFilter"
        >
          {{ value }}
        </SelectMultipleCounter>
      </FiltersMenu>

      <FollawedRadioButtons
        :all-match-route="currentUrl.replace('/follawed', '')"
        :follawed-route="currentUrl.replace('/follawed', '') + '/follawed'"
      />
    </div>

    <h5 class="params-row legislature">
      Politician Relationship Management - 17ème Législature
    </h5>

    <div v-if="store.isFollawedActive" class="params-row">
      <RouterRadioButtons :options="textOptions" />
    </div>

    <PrmNewsParams v-if="route.fullPath.includes('news')" />
    <PrmActivitiesParams v-else-if="route.fullPath.includes('activities')" />

    <div v-else class="params-row">
      <TotalMatched :item-count="store.itemCount" />
      <div class="page-settings">
        <DropdownMenu
          v-model="selectedSorterOption"
          :options="sortersOptions"
          label="Trier par :"
        />

        <DropdownMenu
          v-model="selectedLimitOption"
          :options="limitOptions"
          label="Personnalités par page :"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";

import PrmNewsParams from "./PrmNewsParams.vue";
import PrmActivitiesParams from "./PrmActivitiesParams.vue";
import FiltersMenu from "@follaw-fr/components/filter-menu/organisms/FiltersMenu.vue";
import RouterRadioButtons from "@follaw-fr/components/reusable/molecules/RouterRadioButtons.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";
import SearchAsYouTypeFilter from "@follaw-fr/components/filter-menu/molecules/SearchAsYouTypeFilter.vue";
import TotalMatched from "@follaw-fr/components/reusable/atoms/TotalMatched.vue";
import ScrollTop from "@follaw-fr/components/reusable/atoms/ScrollTop.vue";
import FollawedRadioButtons from "@/follaw-fr/components/reusable/atoms/FollawedRouterRadioButtons.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import { usePrmStore } from "@follaw-fr/stores/prm";
import side_menu from "@follaw-fr/utils/side_menu";

const route = useRoute();
const router = useRouter();
const store = usePrmStore();
const {
  selectedFilters,
  isRegional,
  selectedRegionalFilters,
  selectedNationalFilters,
} = storeToRefs(store);

const fetchSearchAuthors = (event) => {
  if (event.length > 0) {
    store.getSearchAuthorsFilters(event);
  } else {
    store.authors = [];
  }
};

const fetchPrmList = async () => {
  selectedFilters.value.from = 0;
  await store.fetchPrmList();
  if (route.path.includes("/news")) {
    await store.fetchFollawedPrmNews();
  }
  if (route.path.includes("/activities")) {
    await store.fetchFollawedPrmActivities();
  }
};

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: {
      number_of_activities: "Nombre d'activités parlementaires",
      number_of_publications: "Nombre de publications",
      alphabetical: "Ordre alphabétique",
    },
    selectedFilters: isRegional.value
      ? selectedRegionalFilters
      : selectedNationalFilters,
    filterKey: "sort_field",
    onChange: () => fetchPrmList(),
  });

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters,
    filterKey: "size",
    onChange: () => {
      selectedFilters.value.from = 0; // Reset pagination
      fetchPrmList();
    },
  });

const showScrollTop = computed(() => selectedFilters.value.size > 20);
const currentUrl = computed(() => router.currentRoute.value.path);

// Build follawed navbar options from router config
const routerOptions = route.name.toLowerCase().includes("national")
  ? side_menu
      .find((item) => item.name === "PRM")
      .secondary_tabs.find((item) => item.path === "/prm-national").children
  : side_menu
      .find((item) => item.name === "PRM")
      .secondary_tabs.find((item) => item.path === "/prm-regional").children;

const textOptions = route.name.toLowerCase().includes("national")
  ? routerOptions
      .filter((item) => item.name)
      .map((item) => ({
        label: item.name.replace("PRM National Follawed - ", ""),
        value: `/prm-national/${item.path}`,
        exact: true,
      }))
  : routerOptions
      .filter((item) => item.name)
      .map((item) => ({
        label: item.name.replace("PRM Régional Follawed - ", ""),
        value: `/prm-regional/${item.path}`,
        exact: true,
      }));

const addFilterItem = (event) => {
  const targetFilters = selectedFilters.value[event.filter] || [];

  selectedFilters.value = {
    ...selectedFilters.value,
    [event.filter]: [...targetFilters, event.item],
  };
  fetchPrmList();
};

const removeFilterItem = (event) => {
  let targetFilters = selectedFilters.value[event.filter] || [];

  targetFilters = targetFilters.filter((el) => el !== event.item);

  if (targetFilters.length === 0) {
    delete selectedFilters.value[event.filter];
  } else {
    selectedFilters.value = {
      ...selectedFilters.value,
      [event.filter]: targetFilters,
    };
  }
  fetchPrmList();
};

// All filters in wanted display order with their labels
const filtersNames = {
  circo_name: "Circonscription",
  group: "Groupe",
  // The level filter is only present in the regional listing
  ...(store.isRegional && { level: "Niveau" }),
  party: "Parti",
  stakeholder: "Partie prenante",
};

const activeFilters = computed(() => {
  let arr = [];
  // Put all selected options from all filters with said filter name in an array for the SelectedFilters component
  Object.keys(filtersNames).forEach((key) => {
    selectedFilters.value[key]?.forEach((filter) => {
      arr = [...arr, { filter: key, item: filter }];
    });
  });
  selectedFilters.value.authors?.forEach((filter) => {
    arr = [
      ...arr,
      {
        filter: "authors",
        item: filter,
      },
    ];
  });
  return arr;
});

const resetFilter = (event) => {
  delete selectedFilters.value[event];
  fetchPrmList();
};

const resetAuthorFilter = () => {
  store.authors = [];
  delete selectedFilters.value.authors;
  fetchPrmList();
};

const resetAllFilters = () => {
  store.resetFilters();
  fetchPrmList();
};
</script>

<style lang="scss" scoped>
.params-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 10px;
}

.legislature {
  margin: 0;
}
</style>
